import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { testimonials } from '@/mocks/content';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '柠檬导航',
    url: 'https://www.lemonnav.cn',
    description: '柠檬导航是一款精心筛选、分类清晰的优质网站导航平台，汇聚海量实用网址资源。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@lemonnav.cn',
      availableLanguage: ['Chinese'],
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }
    try {
      formData.delete('website_alt');
      const resp = await fetch('https://readdy.ai/api/form/d9spdut8o1vn02ekujkg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await resp.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (resp.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 柠檬导航 | 精选优质网站导航平台"
        description="了解柠檬导航的品牌故事、服务理念和联系方式。柠檬导航精心筛选优质网站，为您提供纯净高效的上网导航体验。收录200+精选网站，覆盖8大分类。"
        keywords="柠檬导航,关于我们,网站导航,联系方式,品牌介绍"
        canonicalUrl="https://www.lemonnav.cn/about"
        schemaData={orgSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Brand intro */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-6">
              关于柠檬导航
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto mb-8">
              柠檬导航诞生于2024年，由一群热爱互联网的产品人创建。我们深知在信息爆炸的时代，找到真正优质的网站有多难。因此，我们坚持「精选优质、宁缺毋滥」的原则，用心筛选每一个收录的网站。
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-star-line text-primary-600 text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">精选优质</h3>
                <p className="text-xs md:text-sm text-foreground-500">每个网站都经过人工审核，确保内容质量和用户体验</p>
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-refresh-line text-accent-600 text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">持续更新</h3>
                <p className="text-xs md:text-sm text-foreground-500">定期巡检已收录网站，确保链接有效、信息准确</p>
              </div>
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-6">
                <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-heart-line text-secondary-600 text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">永久免费</h3>
                <p className="text-xs md:text-sm text-foreground-500">对所有用户完全免费，无付费会员，纯净无广告</p>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 md:py-16 bg-primary-500">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <p className="text-3xl md:text-4xl font-bold text-white">200+</p>
                <p className="text-sm text-white/80 mt-1">精选网站</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-white">8</p>
                <p className="text-sm text-white/80 mt-1">核心分类</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-white">10万+</p>
                <p className="text-sm text-white/80 mt-1">月活用户</p>
              </div>
              <div>
                <p className="text-3xl md:text-4xl font-bold text-white">99.9%</p>
                <p className="text-sm text-white/80 mt-1">服务可用率</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-10">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <i
                        key={j}
                        className={`text-sm ${j < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-xs font-medium">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-4">
              联系我们
            </h2>
            <p className="text-sm md:text-base text-foreground-600 text-center mb-10">
              如有网站推荐、合作意向或任何问题，欢迎通过以下方式联系我们
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div className="text-center p-5 bg-background-50 rounded-xl border border-background-200/70">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                <p className="text-sm font-medium text-foreground-900">contact@lemonnav.cn</p>
              </div>
              <div className="text-center p-5 bg-background-50 rounded-xl border border-background-200/70">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-accent-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">工作时间</p>
                <p className="text-sm font-medium text-foreground-900">每天 9:00 - 18:00</p>
              </div>
              <div className="text-center p-5 bg-background-50 rounded-xl border border-background-200/70">
                <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-line text-secondary-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">办公地址</p>
                <p className="text-sm font-medium text-foreground-900">北京市朝阳区</p>
              </div>
            </div>

            {/* Contact form */}
            <form
              onSubmit={handleSubmit}
              data-readdy-form="true"
              className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8"
            >
              {formStatus === 'success' && (
                <div className="mb-5 px-4 py-3 rounded-lg bg-primary-100 text-primary-800 text-sm">
                  感谢您的留言，我们会尽快回复！
                </div>
              )}
              {formStatus === 'error' && (
                <div className="mb-5 px-4 py-3 rounded-lg bg-red-100 text-red-700 text-sm">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-red-400">*</span>
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-white text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您想说的话（500字以内）"
                ></textarea>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                className="hp-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                className="w-full sm:w-auto px-8 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                发送留言
              </button>
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}